/**
 * The member-limit tier ladder, shared by the app-details screen and the
 * request form so the two cannot describe an app differently.
 *
 * Capacity is earned one band at a time: an app requests the next tier at 50%
 * of its current tier cap, so the form only ever offers the band directly above
 * the one the app is in.
 */

export interface Tier {
  name: string;
  /** `null` means no member limit. */
  cap: number | null;
}

export const TIERS: Tier[] = [
  { name: 'Sandbox', cap: 10 },
  { name: 'Build', cap: 100 },
  { name: 'Growth', cap: 1000 },
  { name: 'Scale', cap: 5000 },
  { name: 'Production', cap: null },
];

/** The 50% rule starts here. Below it there is nothing to demonstrate. */
export const GATE_APPLIES_FROM = 100;

export const capLabel = (cap: number | null): string =>
  cap === null ? 'Unlimited' : cap.toLocaleString();

const PRODUCTION_INDEX = TIERS.findIndex((t) => t.cap === null);

/**
 * Index of the band the app sits in, or -1 when the cap is unknown. Hand-set
 * limits like 25 round DOWN; rounding up would hand out a band the app never
 * qualified for.
 */
export const bandIndexForCap = (cap?: number | null): number => {
  // null is a deliberate "no member limit" setting, which is the top band.
  // undefined means we could not read the limit at all.
  if (cap === null) return PRODUCTION_INDEX;
  if (cap === undefined) return -1;
  let idx = -1;
  TIERS.forEach((t, i) => {
    if (t.cap !== null && t.cap <= cap) idx = i;
  });
  // A limit below the smallest band still belongs to the smallest band.
  return idx === -1 ? 0 : idx;
};

export const bandForCap = (cap?: number | null): Tier | undefined =>
  TIERS[bandIndexForCap(cap)];

export const nextBandForCap = (cap?: number | null): Tier | undefined => {
  const i = bandIndexForCap(cap);
  return i < 0 ? undefined : TIERS[i + 1];
};

/**
 * Connected members needed to move up from the given cap, or `undefined` when
 * the rule does not apply yet.
 *
 * Measured against the **normalised tier cap**, not the app's literal limit: an
 * app hand-set to 1,500 is treated as Growth and needs 500, not 750.
 */
export const membersNeededToAdvance = (
  cap?: number | null,
): number | undefined => {
  const band = bandForCap(cap);
  if (!band || band.cap === null || band.cap < GATE_APPLIES_FROM) {
    return undefined;
  }
  return Math.ceil(band.cap / 2);
};
